import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, Sparkles } from 'lucide-react';
import { IconTile, Button } from '../ui/index.tsx';
import { createPortal } from 'react-dom';
import { lookupGlossaryShort, lookupGlossaryConcept } from '../../constants/awiseKnowledgeBase.ts';

// One-time "meet your adviser" intro. Shown on the FIRST insight panel the user opens in a session
// (any module), then suppressed everywhere else via this sessionStorage flag — so it doesn't repeat
// when they move between Aged Care, Support at Home and Retirement Village.
const ADVISER_INTRO_FLAG = 'awiseAdviserIntroSeen';

const readIntroSeen = () => {
    try {
        return typeof window !== 'undefined' && window.sessionStorage?.getItem(ADVISER_INTRO_FLAG) === '1';
    } catch {
        return true; // if storage is unavailable, don't block the panel with an intro
    }
};

/**
 * Re-arm the adviser intro so it greets the user again the next time an insight panel mounts.
 * Call this when a new client model is opened — keeps the intro "once per client", not once per
 * whole browser session (sessionStorage otherwise persists the dismissal for the entire tab).
 */
export function rearmAdviserIntro() {
    try { window.sessionStorage?.removeItem(ADVISER_INTRO_FLAG); } catch { /* ignore */ }
}

interface AdviserIntroProps {
    onDismiss: () => void;
    userName?: string;
}

/** The shared first-run greeting from AWise, the digital adviser. */
function AdviserIntro({ onDismiss, userName }: AdviserIntroProps) {
    const greeting = userName && userName !== 'there' ? `Hi ${userName}, I'm AWise` : "Hi, I'm AWise";
    return (
        <div>
            <div className="mb-3 flex items-center gap-2.5">
                <IconTile icon={Sparkles} tone="brand" variant="solid" size="sm" />
                <h3 className="m-0 text-[15px] font-semibold text-slate-900">{greeting}</h3>
            </div>
            <p className="mb-2 text-sm leading-relaxed text-slate-600">
                I&apos;m your digital adviser. As you explore options and make changes, I&apos;ll explain
                what&apos;s happening and why.
            </p>
            <p className="mb-4 text-sm leading-relaxed text-slate-600">
                You can move me around, close or reopen me whenever you like.
            </p>
            <Button onClick={onDismiss} variant="brand" fullWidth>Got it — let&apos;s go</Button>
        </div>
    );
}

/**
 * Shared "What's happening?" narrative panel — ONE component used by every module
 * (Aged Care, Support at Home, Retirement Village), in both manual and suggestion-engine modes,
 * so the voice and look are identical everywhere. See docs/UI_UX_HERO_PATH_AUDIT.md (X3b).
 *
 * Exports:
 *   NarrativePanel       the draggable, dockable shell (move-around preserved)
 *   NarrativeCard        the standard insight card (drop-in for the old per-module StrategyCard)
 *   NarrativeCollapsible expandable detail block (drop-in for the old InsightCollapsible)
 *   NarrativeBeat        a single story beat: bold takeaway + plain-language body
 *   JargonTerm           inline term with a plain-language explanation on hover
 */

const DRAG_PANEL_RESERVE = 16; // keep a sliver on-screen when dragged to the edge

/**
 * A phone, at the same 768px line SmallScreenNotice draws — so the two pieces of phone-specific
 * behaviour in the app agree about what a phone is. Tablets and laptops are on the other side of
 * it and keep the floating, draggable panel exactly as it was.
 */
const PHONE_QUERY = '(max-width: 767px)';

const phoneMatches = (): boolean =>
    typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches;

const useIsPhone = (): boolean => {
    const [isPhone, setIsPhone] = useState(phoneMatches);
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mq = window.matchMedia(PHONE_QUERY);
        const onChange = (e: MediaQueryListEvent) => setIsPhone(e.matches);
        mq.addEventListener('change', onChange);
        setIsPhone(mq.matches);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return isPhone;
};

interface PanelPos {
    x: number;
    y: number;
}

interface NarrativePanelProps {
    title?: React.ReactNode;
    markUrl?: string;
    width?: number;
    initialPos?: PanelPos;
    userName?: string;
    onBackToDashboard?: () => void;
    children?: React.ReactNode;
}

export function NarrativePanel({
    title = "What's happening?",
    markUrl,
    width = 360,
    initialPos = { x: 60, y: 120 },
    userName,
    onBackToDashboard,
    children,
}: NarrativePanelProps) {
    const isPhone = useIsPhone();
    // Open on arrival on a tablet or laptop, exactly as before. On a phone it starts as the
    // "Show insights" chip instead: the screen is the scarce resource there, and an overlay that
    // opens itself across the content is a poor greeting — particularly right after the
    // small-screen notice has said this section is a squeeze. Read once, at mount, so resizing
    // never overrides what the reader has since chosen.
    const [showPanel, setShowPanel] = useState(() => !phoneMatches());
    const [showIntro, setShowIntro] = useState(() => !readIntroSeen());
    const [pos, setPos] = useState<PanelPos>(initialPos);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{ offsetX: number; offsetY: number } | null>(null);

    const dismissIntro = () => {
        try { window.sessionStorage?.setItem(ADVISER_INTRO_FLAG, '1'); } catch { /* ignore */ }
        setShowIntro(false);
    };

    /** Keeps the panel on screen: the same clamp the drag uses, so both agree. */
    const clampToViewport = (nx: number, ny: number): PanelPos => ({
        x: Math.max(0, Math.min(window.innerWidth - (width - DRAG_PANEL_RESERVE), nx)),
        y: Math.max(0, Math.min(window.innerHeight - 200, ny)),
    });

    // POINTER events, not mouse events. Dragging was `mousedown`/`mousemove`, which a touchscreen
    // never fires for a drag gesture — so on an iPad the panel could not be moved at all, and
    // trying just scrolled the page behind it (adviser question, 2026-08-19). Pointer events cover
    // mouse, touch and pen through one path; `touch-none` on the handle below stops the browser
    // claiming the gesture for scrolling before it reaches us.
    const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only
        const rect = e.currentTarget.getBoundingClientRect();
        dragRef.current = { offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return undefined;
        const move = (e: PointerEvent) => {
            if (!dragRef.current) return;
            setPos(clampToViewport(e.clientX - dragRef.current.offsetX, e.clientY - dragRef.current.offsetY));
        };
        const up = () => {
            setIsDragging(false);
            dragRef.current = null;
        };
        document.addEventListener('pointermove', move);
        document.addEventListener('pointerup', up);
        // A touch drag interrupted by the system (a call, a gesture taking over) ends as a cancel
        // and never as an up; without this the panel would stay stuck to the finger.
        document.addEventListener('pointercancel', up);
        return () => {
            document.removeEventListener('pointermove', move);
            document.removeEventListener('pointerup', up);
            document.removeEventListener('pointercancel', up);
        };
    }, [isDragging, width]);

    // On arrival as well as on drag: the default position (60px in, 360px wide) hangs about 30px
    // off the right edge of a narrow screen, so the panel opened partly out of view.
    useEffect(() => {
        setPos((p) => clampToViewport(p.x, p.y));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);

    // Rotating an iPad, or any other viewport change, can leave a panel parked near an edge
    // stranded off screen — with no way back short of closing and reopening it. Re-clamp instead.
    useEffect(() => {
        const onResize = () => setPos((p) => clampToViewport(p.x, p.y));
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width]);

    if (!showPanel) {
        return (
            <button
                type="button"
                onClick={() => setShowPanel(true)}
                className="fixed top-5 left-5 z-[10000] flex items-center gap-2 rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white shadow-cardlg hover:bg-brand-ink transition-colors"
            >
                <Lightbulb size={16} /> Show insights
            </button>
        );
    }

    return (
        <div
            className={`fixed z-[10000] flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-cardlg${
                isPhone ? ' inset-x-2 bottom-2' : ''
            }`}
            /* A phone gets a bottom sheet: full width less a margin, anchored low, its own scroll.
               A floating window is a desktop idea — once it fills the screen there is nowhere to
               drag it to, and the gesture only fights the scrolling of its own content. Tablets
               and laptops keep the positioned, resizable-by-drag window unchanged. */
            style={isPhone ? { maxHeight: '70vh' } : { left: pos.x, top: pos.y, width, maxHeight: '80vh' }}
        >
            <div
                className={`flex ${isPhone ? '' : 'cursor-move touch-none '}select-none items-center justify-between gap-2 bg-brand-navy px-4 py-3 text-white`}
                onPointerDown={isPhone ? undefined : handlePointerDown}
            >
                <div className="flex items-center gap-2">
                    {markUrl && <img src={markUrl} alt="" width={18} height={18} className="block rounded-sm brightness-0 invert" />}
                    <span className="text-[15px] font-bold">{title}</span>
                </div>
                <button
                    type="button"
                    onClick={() => setShowPanel(false)}
                    title="Close"
                    className="text-xl leading-none text-white/70 transition-colors hover:text-white"
                >
                    ×
                </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-canvas p-4 text-[15px] text-slate-700">
                {showIntro ? <AdviserIntro onDismiss={dismissIntro} userName={userName} /> : (
                    <>
                        {children}
                        {onBackToDashboard && (
                            <button
                                type="button"
                                onClick={onBackToDashboard}
                                className="mt-4 w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-slate-50"
                            >
                                ← Back to Dashboard
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

interface NarrativeCardProps {
    title?: React.ReactNode;
    icon?: IconComponent;
    iconColor?: string;
    children?: React.ReactNode;
}

// iconColor is accepted but ignored — every card uses the one brand IconTile marker (no pastels),
// matching the Aged Care InsightSection look so all three modules read the same way.
export function NarrativeCard({ title, icon: Icon, children }: NarrativeCardProps) {
    return (
        <div className="mb-4 rounded-2xl border border-slate-200/70 bg-white p-4 shadow-card">
            {title && (
                <div className="mb-3 flex items-center gap-2.5 border-b border-slate-100 pb-3">
                    {Icon && <IconTile icon={Icon} tone="brand" variant="solid" size="sm" />}
                    <h3 className="m-0 text-[15px] font-semibold text-slate-900">{title}</h3>
                </div>
            )}
            <div className="space-y-1.5 text-sm leading-relaxed text-slate-600">{children}</div>
        </div>
    );
}

interface NarrativeCollapsibleProps {
    title?: React.ReactNode;
    children?: React.ReactNode;
    defaultOpen?: boolean;
}

export function NarrativeCollapsible({ title, children, defaultOpen = false }: NarrativeCollapsibleProps) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="mb-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="flex w-full items-center justify-between gap-2.5 bg-slate-50 px-3.5 py-3 text-left transition-colors hover:bg-slate-100"
            >
                <span className="text-[15px] font-semibold leading-snug text-slate-900">{title}</span>
                {open ? (
                    <ChevronUp size={18} className="flex-shrink-0 text-slate-500" />
                ) : (
                    <ChevronDown size={18} className="flex-shrink-0 text-slate-500" />
                )}
            </button>
            {open && <div className="px-3.5 pb-3.5 pt-1 text-sm leading-relaxed text-slate-600">{children}</div>}
        </div>
    );
}

/**
 * A single story beat in the new plain-language voice: a bold takeaway, then the body
 * (the "why" and "what it means for you"). Use inside a NarrativeCard.
 */
interface NarrativeBeatProps {
    lead?: React.ReactNode;
    children?: React.ReactNode;
}

export function NarrativeBeat({ lead, children }: NarrativeBeatProps) {
    return (
        <div className="mb-3 last:mb-0">
            {lead && <p className="mb-1 font-semibold text-slate-900">{lead}</p>}
            {children && <div className="text-sm leading-relaxed text-slate-600">{children}</div>}
        </div>
    );
}

/**
 * Inline jargon term with a plain-language explanation shown on hover.
 * Pass `children` (a string) to override; otherwise the tooltip is resolved from the shared
 * glossary (awiseKnowledgeBase) by `term` — acronyms and full names both work (e.g. "RAD").
 * If no explanation is found, the term renders as plain text (no misleading dotted underline).
 */
interface JargonTermProps {
    term: string;
    children?: React.ReactNode;
    /**
     * Where the explanation opens.
     *
     * 'inline' (default) drops it in a block box directly under the sentence that used the term.
     * That is right inside flowing prose, where the box inherits the paragraph's width.
     *
     * 'dialog' opens the centred pop-up instead, and is for call sites in a NARROW COLUMN. A block
     * box sets its flex item's max-content width, so a paragraph of explanation inside a `shrink-0`
     * column makes that column as wide as the text: on the Suggestion Engine cards, tapping "how is
     * this projected?" widened the figures column until the strategy heading beside it — which
     * carries `min-w-0` — collapsed to one word per line, with the explanation overflowing the card
     * (owner-caught, 2026-08-27). Prose does not belong in a 150px column at all.
     */
    expandAs?: 'inline' | 'dialog';
    /** Words to show instead of the term — "what's this?" on a plan-piece row, where the term is already the row's name. */
    label?: React.ReactNode;
}

export function JargonTerm({ term, children, expandAs = 'inline', label }: JargonTermProps) {
    /*
     * TAP to expand, not hover. This was a `title` tooltip, which has two failures the owner's
     * client meetings surfaced (2026-08-24): it does not exist on a tablet — and advisers demo this
     * product on iPads — and even on desktop nothing invites the hover, so the explanation may as
     * well not be there. A tap opens the one-sentence explanation IN PLACE, directly under the
     * sentence that used the term; a second tap closes it. The glossary content is unchanged — the
     * single vetted source in awiseKnowledgeBase stays the single source.
     *
     * Rendered as <button> + block <span>, both valid inside a <p>, so every existing call site
     * keeps working without markup changes.
     */
    const [open, setOpen] = useState(false);
    const [fullOpen, setFullOpen] = useState(false);
    const explanation =
        typeof children === 'string' ? children : lookupGlossaryShort(term) || undefined;
    /*
     * TWO TIERS (owner request, 2026-08-24). The inline box works for a sentence; it does not work
     * for a concept that genuinely needs a paragraph. So: the inline box always shows the SHORT
     * explanation, and when the vetted glossary holds a materially longer one, the box offers
     * "Read the full explanation", which opens a centred pop-up. Same single content source
     * (awiseKnowledgeBase) for both tiers - nothing is written twice.
     */
    const concept = lookupGlossaryConcept(term);
    const fullText =
        concept?.glossary && explanation && concept.glossary.length > explanation.length + 120
            ? concept.glossary
            : null;
    if (!explanation) return <>{term}</>;
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                title={explanation}
                style={{ fontFamily: 'inherit', fontSize: 'inherit', lineHeight: 'inherit' }}
                className="inline border-0 bg-transparent p-0 text-left font-semibold text-blue-700 underline decoration-dotted decoration-blue-400 underline-offset-2 cursor-pointer hover:text-blue-900 focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm"
            >
                {label ?? term}
            </button>
            {open && expandAs === 'inline' && (
                <span className="mt-1.5 mb-1 block whitespace-pre-line rounded-lg border border-blue-100 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700">
                    {explanation}
                    {fullText && (
                        <button
                            type="button"
                            onClick={() => setFullOpen(true)}
                            className="mt-1 block border-0 bg-transparent p-0 text-left text-xs font-semibold text-blue-700 underline underline-offset-2 cursor-pointer hover:text-blue-900"
                        >
                            Read the full explanation
                        </button>
                    )}
                </span>
            )}
            {/* In a narrow column the explanation goes straight to the pop-up — it is rendered
                through a portal, so it cannot size the column it was opened from. */}
            {open && expandAs === 'dialog' && (
                <GlossaryDialog
                    title={concept?.term || term}
                    body={fullText || explanation}
                    onClose={() => setOpen(false)}
                />
            )}
            {fullOpen && fullText && (
                <GlossaryDialog title={concept?.term || term} body={fullText} onClose={() => setFullOpen(false)} />
            )}
        </>
    );
}

/**
 * The vetted glossary is written for an LLM as much as for a screen: it carries "LABEL:" headings
 * in capitals and "- " bullets whose first words are shouted. Read straight off the file it looked
 * like a dump (owner, 24 Sep 2026: "this looks terrible"). This renders each block as a reader
 * expects: a heading in sentence case, a paragraph, or a list whose lead words are bold rather
 * than capitalised. The text itself is untouched — one source, two readers.
 */
const sentenceCase = (shout: string): string => {
    const t = shout.trim();
    if (!t) return t;
    const lower = t.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1)
        .replace(/\brcv\b/g, 'RCV')
        .replace(/\bcentrelink\b/g, 'Centrelink')
        .replace(/\bage pension\b/g, 'Age Pension');
};
const isShouted = (t: string): boolean => /^[A-Z0-9 ,'()\/&-]{4,}$/.test(t.trim()) && /[A-Z]/.test(t);

function GlossaryLine({ text }: { text: string }) {
    // "SHOUTED LEAD. The rest." → bold lead in sentence case, then the rest as written.
    const m = /^([A-Z][A-Z0-9 ,'()\/&-]{3,}?)([.:])\s+(.*)$/s.exec(text.trim());
    if (m && isShouted(m[1]!)) {
        return (
            <>
                <span className="font-semibold text-slate-900">{sentenceCase(m[1]!)}{m[2] === ':' ? ':' : '.'}</span> {m[3]}
            </>
        );
    }
    return <>{text}</>;
}

function GlossaryBlock({ block }: { block: string }) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return null;
    // A heading on its own line ("WHAT YOU GIVE UP:") followed by bullets.
    const headingOnly = lines.length > 1 && /^[A-Z0-9 ,'()\/&-]+:$/.test(lines[0]!) && lines.slice(1).every((l) => l.startsWith('- '));
    const items = (headingOnly ? lines.slice(1) : lines).filter((l) => l.startsWith('- '));
    const isList = items.length > 0 && items.length === (headingOnly ? lines.length - 1 : lines.length);
    if (isList) {
        return (
            <div>
                {headingOnly && <p className="mb-1 text-sm font-bold text-slate-900">{sentenceCase(lines[0]!.replace(/:$/, ''))}</p>}
                <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-slate-700">
                    {items.map((l, j) => <li key={j}><GlossaryLine text={l.slice(2)} /></li>)}
                </ul>
            </div>
        );
    }
    return <p className="text-sm leading-relaxed text-slate-700"><GlossaryLine text={lines.join(' ')} /></p>;
}

/**
 * The full-explanation pop-up. Rendered through a portal so a JargonTerm sitting inside a <p>
 * never nests a <div> in a paragraph (invalid HTML the browser silently "repairs" by splitting
 * the paragraph). Closes on the X, the backdrop, or Escape.
 */
function GlossaryDialog({ title, body, onClose }: { title: string; body: string; onClose: () => void }) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);
    return createPortal(
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div
                className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
                    <h3 className="text-base font-bold text-slate-900">{title}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 rounded-md border-0 bg-transparent px-1.5 text-lg leading-none text-slate-400 cursor-pointer hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        &times;
                    </button>
                </div>
                <div className="max-h-[65vh] overflow-y-auto px-5 py-4 space-y-3">
                    {/* The vetted glossary text may carry light structure: blank lines separate
                        paragraphs, and a run of lines starting with "- " renders as bullets. Same
                        single content source as the AWise grounding - only the display differs. */}
                    {body.split(/\n\s*\n/).map((block, i) => <GlossaryBlock key={i} block={block} />)}
                </div>
                <div className="border-t border-slate-100 px-5 py-3 text-right">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 cursor-pointer hover:bg-slate-50"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default NarrativePanel;
